import { HttpHeaders, HttpResponse } from '@angular/common/http';
import { Params } from '@angular/router';
import { saveAs } from 'file-saver';
import { Buffer } from 'buffer';
import * as XLSX from 'xlsx';

export class Utils {
    static prettyFileSize(bytes: number): string {
        const div = 1024;
        const units = ['B', 'KB', 'MB', 'GB'];

        let unit = units[0];
        let size = bytes;
        let curr: number;

        while ((curr = size / div) > 1) {
            size = curr;
            units.shift();
        }

        return `${+size.toFixed(2)}${units.length ? units[0] : unit}`;
    }

    /**
     * Replace tokens in a string.
     * @param {string} input
     * @param {string[]} replacements
     * @returns {string}
     */
    static formatString(input: string, replacements: any): string {
        replacements = [].concat(replacements);
        return input.replace(/{(\d+)}/g, (match, n) => replacements[n]);
    };

    /**
     * Convert base64 string to a blob.
     * @param base64
     * @param mimeType
     * @returns {Blob}
     */
    static base64ToBlob(base64: string, mimeType: string = '') {
        let sliceSize = 1024;
        let byteChars = window.atob(base64);
        let byteArrays = [];

        for (let offset = 0, len = byteChars.length; offset < len; offset += sliceSize) {
            var slice = byteChars.slice(offset, offset + sliceSize);

            let byteNumbers = new Array(slice.length);

            for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
            }

            let byteArray = new Uint8Array(byteNumbers);

            byteArrays.push(byteArray);
        }

        return new Blob(byteArrays, { type: mimeType });
    }

    /**
     * Get a date instance from an input.
     * @param date
     */
    static getInputDate(date: any): Date {
        if (!date)
            return null;

        if (date instanceof Date)
            return date;

        if (date['toDate'])
            return date.toDate();

        return new Date(date);
    }

    /**
     * Get input date string in a "yyyy-mm-dd" format.
     * @param date
     */
    static getInputDateString(date: any): string {
        let t = Utils.getInputDate(date);

        if (!t) return '';

        return `${t.getFullYear()}-${('0' + (t.getMonth() + 1)).slice(-2)}-${('0' + (t.getDate())).slice(-2)}`;
    }

    /**
     * Get input date string in a "yyyy-mm-ddTHH:mm:ss" format.
     * @param date
     */
    static getInputDateTimeString(dateTime: any): string {
        let t = Utils.getInputDate(dateTime);

        if (!t) return '';

        return `${t.getFullYear()}-${('0' + (t.getMonth() + 1)).slice(-2)}-${('0' + (t.getDate())).slice(-2)}`
            + `T${('0' + t.getHours()).slice(-2)}:${('0' + t.getMinutes()).slice(-2)}:${('0' + t.getSeconds()).slice(-2)}`;
    }

    static parseDateTime(dateTime: any): {
        year: number,
        yearString: string,
        month: number,
        monthString: string,
        date: number,
        dateString: string,
        hours: number,
        hoursString: string,
        minutes: number,
        minutesString: string,
        seconds: number,
        secondsString: string
    } | undefined {
        let t = Utils.getInputDate(dateTime);

        if (!t) return undefined;

        const y = t.getFullYear();
        const m = t.getMonth();
        const d = t.getDate();
        const hh = t.getHours();
        const mm = t.getMinutes();
        const ss = t.getSeconds();

        return {
            year: y,
            yearString: y.toString(),
            month: m,
            monthString: ('0' + (m + 1)).slice(-2),
            date: d,
            dateString: ('0' + d).slice(-2),
            hours: hh,
            hoursString: ('0' + hh).slice(-2),
            minutes: mm,
            minutesString: ('0' + mm).slice(-2),
            seconds: ss,
            secondsString: ('0' + ss).slice(-2)
        };
    }

    static arrayToDictionary<TItem>(array: TItem[], getKey: (item: TItem) => string): { [key: string]: TItem } {
        let dict: { [key: string]: TItem } = {};

        array.forEach(t => {
            let key = getKey(t);
            dict[key] = t;
        });

        return dict;
    }

    /**
     * Get the date object from the value. Useful when dealing with moment dates bound to material datepicker.
     * @param value
     */
    static ensureDate(value: any): Date {
        if (!value) return undefined;
        if (value instanceof Date) return value;
        if (value.toDate) return value.toDate();
        if (typeof value === 'string') return new Date(value);
    }

    static getBrowserInfo = () => {
        const ua = navigator.userAgent;
        let M = ua.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) || [];

        let tem: string[] = [];

        if(/trident/i.test(M[1])){
            tem = /\brv[ :]+(\d+)/g.exec(ua) || [];
            return {
                name:'IE',
                version:(tem[1] || '')
            };
        }
        if(M[1]=== 'Chrome'){
            tem = ua.match(/\b(OPR|Edge)\/(\d+)/);
            if(tem != null) {
                return {
                    name:tem[1].replace('OPR', 'Opera'),
                    version:tem[2]
                };
            }
        }

        M = M[2]? [M[1], M[2]]: [navigator.appName, navigator.appVersion, '-?'];

        if((tem = ua.match(/version\/(\d+)/i))!= null && M[0]!== 'Chrome'){
            M.splice(1, 1, tem[1]);
        }
        return {
            ua: ua,
            name:M[0],
            version:M[1]
        };
    }

    /**
     * Escape regular expression special symbols.
     * @param pattern
     */
    static escapeRegExp(pattern: string) {
        return pattern.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
    }

    /**
     * Validate a password.
     * @param password
     * @param options
     */
    static validatePassword(password: string, options: {
        minLength: number,
        maxLength: number,
        uppercase?: boolean,
        lowercase?: boolean,
        digit?: boolean,
        special?: boolean
    } = { minLength: 8, maxLength: 16 }): boolean {
        if (!password || !options) return false;

        if (password.length < options.minLength) return false;
        if (password.length > options.maxLength) return false;

        if (options.uppercase && /[A-Z]/.test(password) == false) return false;
        if (options.lowercase && /[a-z]/.test(password) == false) return false;
        if (options.digit && /[0-9]/.test(password) == false) return false;

        const special = '!"#$%&\'()*+,-./:;<=>?@[\]^_`{|}~';
        if (options.special && password.split('').some(t => special.indexOf(t) != -1) == false) return false;

        return true;
    }

    /**
     * Sanitize object parameter values.
     * @param data
     */
    //static sanitizeObjectData<T>(data: T): T {
    //    let result = JSON.parse(JSON.stringify(data));

    //    Object.keys(result).forEach(k => {
    //        if (result[k]) {
    //            if (typeof result[k] === 'string')
    //                result[k] = encodeURIComponent(result[k]);
    //            else if (typeof result[k] === 'object')
    //                result[k] = Utils.sanitizeObjectData(result[k]);
    //        }
    //    });

    //    return result;
    //}

    static sanitizeQueryParams(options: any): any {
        if (!options) return {};

        const params: any = {};

        for (const k in options) {
            const val = options[k];

            if (val !== undefined && val !== null) {
                if (typeof val == 'string') {
                    if (val) params[k] = val;
                } else {
                    params[k] = val;
                }
            }
        }

        return params;
    }

    /**
     * Download a file from HTTP response.
     * @param response HTTP response requsted with { responseType: 'blob', observe: 'response' } options.
     */
    static downloadResponseFile(response: HttpResponse<Blob>): void {
        const filename = this.readFileNameFromHeaders(response.headers);
        saveAs(response.body, filename);
    }

    /**
     * Read file name from HTTP headers.
     * @param headers
     */
    static readFileNameFromHeaders(headers: HttpHeaders): string {
        let filename = undefined;
        const disp = headers.get('content-disposition');

        const utf8Match = disp.match(/filename\*=UTF-8''([\w%\-\.]+)(?:; ?|$)/i);

        if (utf8Match.length > 1) {
            filename = decodeURIComponent(utf8Match[1]);
        } else {
            const match = disp.match(/filename="?([^"]+)"?;/);

            if (match.length > 1) {
                filename = match[1];
            }
        }

        return filename;
    }

    static getMaxLengthValidationPattern(value: string, max: number): string {
        return (!value || (value.length <= max)) ? '' : '$^';
    }

    /**
     * Get month date index.
     * @param monthDate Month date in MM-dd format
     * @returns
     */
    static getMonthDateIndex(monthDate: string): number {
        const parts = monthDate.split('-').map(t => +t);
        return parts[0] * 1000 + parts[1];
    }

    /**
     * Check if a date is between month dates.
     * @param date Date to check
     * @param monthDateFrom Month date from in MM-dd format
     * @param monthDateTo Month date to in MM-dd format
     * @returns
     */
    static isMonthDateBetween(date: Date, monthDateFrom: string, monthDateTo: string): boolean {
        const from = Utils.getMonthDateIndex(monthDateFrom);
        const to = Utils.getMonthDateIndex(monthDateTo);
        const now = Utils.getMonthDateIndex(`${date.getMonth() + 1}-${date.getDate()}`);

        if (from > to) {
            const minIndex = Utils.getMonthDateIndex('01-01');
            const maxIndex = Utils.getMonthDateIndex('12-31');

            return (now >= from && now <= maxIndex) || (now >= minIndex && now <= to);
        } else {
            return now >= from && now <= to;
        }
    }

    /**
     * Find a parameter value by case-insensitive key.
     * @param params
     * @param key
     * @returns
     */
    static findParam(params: Params, key: string): string {
        const k = Object.keys(params).find(t => t.toLowerCase() == key.toLowerCase());
        return k ? params[k] : undefined;
    }

    /**
     * Convert image data URL to a File.
     * @param dataUrl
     * @param fileName
     */
    //https://stackoverflow.com/questions/35940290/how-to-convert-base64-string-to-javascript-file-object-like-as-from-file-input-f
    static dataUrlToFile(dataUrl: string, fileName: string): File | undefined {
        const arr = dataUrl.split(',');
        if (arr.length < 2) { return undefined; }
        const mimeArr = arr[0].match(/:(.*?);/);
        if (!mimeArr || mimeArr.length < 2) { return undefined; }
        const mime = mimeArr[1];
        const buff = Buffer.from(arr[1], 'base64');
        return new File([buff], fileName, { type: mime });
    }

    /**
     * Create HTML image element.
     * @param url
     * @returns
     */
    static urlToImage(url: string): Promise<HTMLImageElement> {
        return new Promise<HTMLImageElement>((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'use-credentials';
            img.src = url + (url.startsWith('data:') ? '' : `?${(new Date()).getTime()}`);
            img.onload = () => resolve(img);
            img.onerror = reject;
        });
    }

    /**
     * Convert HTML image element to data URL.
     * @param img
     * @returns
     */
    static imageToDataUrl(img: HTMLImageElement): string {
        const canvas = document.createElement('canvas');
        canvas.height = img.naturalHeight;
        canvas.width = img.naturalWidth;

        canvas.getContext('2d').drawImage(img, 0, 0);

        return canvas.toDataURL();
    }

    /**
     * Convert raw image URL to data URL.
     * @param img
     * @returns
     */
    static imageUrlToDataUrl(url: string): Promise<string> {
        return new Promise<string>((resolve, reject) => {
            this.urlToImage(url).then(img => {
                const dataUrl = this.imageToDataUrl(img);
                resolve(dataUrl);
            });
        });
    }

    //static resizeImage(img: HTMLImageElement, targetWidth: number): string {
    //    const canvas = document.createElement('canvas');
    //    const ctx = canvas.getContext("2d");
    //    const oc = document.createElement('canvas');
    //    const octx = oc.getContext('2d');

    //    canvas.width = targetWidth; // destination canvas size
    //    canvas.height = canvas.width * img.height / img.width;

    //    let cur = {
    //        width: Math.floor(img.width * 0.5),
    //        height: Math.floor(img.height * 0.5)
    //    };

    //    oc.width = cur.width;
    //    oc.height = cur.height;

    //    octx.drawImage(img, 0, 0, cur.width, cur.height);

    //    while (cur.width * 0.5 > targetWidth) {
    //        cur = {
    //            width: Math.floor(cur.width * 0.5),
    //            height: Math.floor(cur.height * 0.5)
    //        };
    //        octx.drawImage(oc, 0, 0, cur.width * 2, cur.height * 2, 0, 0, cur.width, cur.height);
    //    }

    //    ctx.drawImage(oc, 0, 0, cur.width, cur.height, 0, 0, canvas.width, canvas.height);

    //    return canvas.toDataURL();
    //}

    /**
     * Export JSON data to XLSX file.
     * @param data
     * @param fileName
     */
    static jsonToXlsx<T>(data: T[], fileName: string): void {
        const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(data);
        this.exportToXlsx(worksheet, fileName);
    }

    /**
     * Export array data to XLSX file.
     * @param data
     * @param fileName
     */
    static arrayToXlsx(data: any[][], fileName: string): void {
        const worksheet: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(data);
        this.exportToXlsx(worksheet, fileName);
    }

    /**
     * Remove the punctuation symbols from a string.
     * @param str
     * @returns
     */
    static stripPunctuation(str: string): string {
        if (!str) return '';
        return str.replace(/[,.-:;!?()'"]/g, '');
    }

    private static exportToXlsx(worksheet: XLSX.WorkSheet, fileName: string): void {
        const workbook: XLSX.WorkBook = { Sheets: { 'data': worksheet }, SheetNames: ['data'] };
        const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob: Blob = new Blob([excelBuffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
        });

        saveAs(blob, fileName + '.xlsx');
    }
}
